import React, { useState } from 'react';
import { RefreshCcw, Copy, Check, Key, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { useAuth } from '../../contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { retryOn500 } from '../../utils/retryOn500';

const APIKeyManagement = () => {
  const [showFullKey, setShowFullKey] = useState(false);
  const [copied, setCopied] = useState(false);
  const { token, hasRole } = useAuth();
  const queryClient = useQueryClient();

  // Query for fetching current API key
  const { data: apiKeyData, isLoading } = useQuery({
    queryKey: ['api-key'],
    queryFn: async () => {
      const response = await retryOn500({
        requestFn: () => fetch(`${process.env.REACT_APP_API_URL}/admin/api-key`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
      });
      
      if (!response.ok) throw new Error('Failed to fetch API key');
      return response.json();
    },
    enabled: hasRole('admin')
  });

  // Mutation for generating new API key
  const generateKey = useMutation({
    mutationFn: async () => {
      const response = await retryOn500({
        requestFn: () => fetch(`${process.env.REACT_APP_API_URL}/admin/api-key/generate`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
      });
      
      if (!response.ok) throw new Error('Failed to generate new API key');
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['api-key'], data);
      setShowFullKey(true); // Show the full key when a new one is generated
    }
  });

  const handleCopyKey = async () => {
    if (apiKeyData?.key) {
      await navigator.clipboard.writeText(showFullKey ? apiKeyData.key : apiKeyData.maskedKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!hasRole('admin')) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Access Denied</AlertTitle>
        <AlertDescription>
          You do not have permission to manage API keys.
        </AlertDescription>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto p-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-900">API Key Management</h2>
        <p className="mt-1 text-sm text-slate-500">
          Manage your API keys for external integrations
        </p>
      </div>

      <div className="bg-white shadow rounded-lg divide-y divide-gray-200">
        {/* Current API Key Section */}
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Key className="h-5 w-5 text-slate-400" />
              <h3 className="text-lg font-medium text-slate-900">Current API Key</h3>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowFullKey(!showFullKey)}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                {showFullKey ? (
                  <>
                    <EyeOff className="h-4 w-4 mr-2" />
                    Hide
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4 mr-2" />
                    Show
                  </>
                )}
              </button>
              <button
                onClick={handleCopyKey}
                disabled={!apiKeyData?.key}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4 mr-2 text-green-500" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="mt-4">
            {apiKeyData?.key ? (
              <div className="flex items-center space-x-2">
                <code className="block p-4 bg-gray-50 rounded-md text-sm font-mono flex-1">
                  {showFullKey ? apiKeyData.key : apiKeyData.maskedKey}
                </code>
              </div>
            ) : (
              <div className="p-4 bg-gray-50 rounded-md text-sm text-gray-500">
                No API key generated yet
              </div>
            )}
          </div>

          {apiKeyData?.createdAt && (
            <div className="mt-2 text-sm text-gray-500">
              Created: {new Date(apiKeyData.createdAt).toLocaleString()}
            </div>
          )}
          {apiKeyData?.lastUsed && (
            <div className="text-sm text-gray-500">
              Last used: {new Date(apiKeyData.lastUsed).toLocaleString()}
            </div>
          )}
        </div>

        {/* Generate New Key Section */}
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-slate-900">Generate New Key</h3>
              <p className="mt-1 text-sm text-slate-500">
                Generate a new API key. This will invalidate the current key.
              </p>
            </div>
            <button
              onClick={() => generateKey.mutate()}
              disabled={generateKey.isLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {generateKey.isLoading ? (
                <>
                  <RefreshCcw className="animate-spin h-4 w-4 mr-2" />
                  Generating...
                </>
              ) : (
                <>
                  <RefreshCcw className="h-4 w-4 mr-2" />
                  Generate New Key
                </>
              )}
            </button>
          </div>

          {generateKey.isError && (
            <Alert variant="destructive" className="mt-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>
                Failed to generate new API key. Please try again.
              </AlertDescription>
            </Alert>
          )}
        </div>

        {/* Security Notice */}
        <div className="p-6 bg-yellow-50">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Security Notice
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <ul className="list-disc pl-5 space-y-1">
                  <li>Keep your API key secure and never share it publicly</li>
                  <li>Rotate your key regularly for enhanced security</li>
                  <li>Generating a new key will invalidate the previous key</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default APIKeyManagement;